import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { handleResponseErrors } from "@helpers/Actions";
import CrudApi from "@crudApi";
import * as moment from "moment";
import { ls } from "@src/Services/Helpers/LocalStorageEnc";
import { queryClient } from "@providers/QueryClient";
import { store } from "@src/redux/store";
import { getCompanyProfile } from "@src/views/SystemSettings/CompanyAccount/store";

export const handleUserLogin = createAsyncThunk(
  "loginUser/login",
  async ({ email, password }, { fulfillWithValue, rejectWithValue }) => {
    const response = await CrudApi({ maxRateLimit: 3, useChunk: false , useFormData: false, saveSnapshot: false }).post("/api/auth/login", { email, password })
      .then(res => res)
      .catch(e => handleResponseErrors(e));
    if (response?.status === 200 && response?.data?.data?.access_token) {
      return fulfillWithValue(response?.data?.data);
    }
    return rejectWithValue(response);
  }
);

export const handleUserLogout = createAsyncThunk(
  "loginUser/logout",
  async (_, { fulfillWithValue, rejectWithValue }) => {
    const response = await CrudApi({ saveSnapshot: false }).get("/api/auth/logout")
      .then((res) => res)
      .catch((e) => handleResponseErrors(e));
    if (response?.status === 200) {
      return fulfillWithValue(response?.data);
    }
    return rejectWithValue(response);
  }
);

export const authMe = createAsyncThunk(
  "auth/authMe",
  async (_, { dispatch, fulfillWithValue, rejectWithValue }) => {
    const response = await CrudApi({ saveSnapshot: false }).get("/api/auth/me");
    if (response?.status === 200) {
      // The backend bumps `company_profile_version` on every company profile
      // change, so a mismatch (or a missing local copy) is the only signal we
      // need to refetch the cached profile.
      const { company_profile_version } = response?.data?.data || {};
      const currentVersion = store.getState()?.company?.company?.company_profile_version;
      if (company_profile_version !== currentVersion) {
        dispatch(getCompanyProfile());
      }
      return fulfillWithValue(response?.data?.data);
    }
    return rejectWithValue(response);
  }
);

const updateUserAndPermissions = (state, action , type = 'authMe') => {
  const permissions = action.payload?.permissions || []
  state.permissions = [...permissions , 'read_profile','edit_profile'];
  state.user = action.payload;

  const mainBranch = action.payload?.branch;
  const filteredBranches = ls.getItem('filteredBranches') || [];
  const isBranchExist = !!filteredBranches?.find(item => item == mainBranch?.id) || type === 'authMe';
  const accessibleBranches = action.payload?.accessibleBranches || [];
  ls.setItem('selectedBranchesOptions', [mainBranch , ...accessibleBranches])

  if (!isBranchExist) { // on first login || if main branch is not in the filteredBranches
    ls.setItem('filteredBranches', [...filteredBranches, mainBranch?.id]);
  }
};

const clearUserData = (state) => {
  state.user = {};
  state.isLogin = false;
  state.loginAt= undefined;
  state.permissions = [];
  state.access_token = undefined;
  state.refresh_token = undefined;
  ls.clear();
  queryClient.clear();
};

export const loginUserSlice = createSlice({
  name: "loginUser",
  initialState: {
    user: {},
    permissions: [],
    isLogin: false,
    loginAt: undefined,
    access_token: undefined,
    refresh_token: undefined,
  },
  reducers: {
    setTokens: (state, action) => {
      state.access_token = action.payload?.access_token;
      state.refresh_token = action.payload?.refresh_token;
    },
    clearAuth: (state) => {
      clearUserData(state);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(handleUserLogin.fulfilled, (state, action) => {
        const user  = action.payload;
        state.isLogin = true;
        updateUserAndPermissions(state, action, 'login');
        state.loginAt = moment();
        state.access_token = user.access_token;
        state.refresh_token = user.refresh_token;
      })
      .addCase(handleUserLogin.rejected, (state, action) => {
        state.isLogin = false;
        state.user = {};
        state.permissions = [];
        state.loginAt = undefined;
        state.access_token = undefined;
        state.refresh_token = undefined;
      })
      .addCase(handleUserLogout.fulfilled, (state, action) => {
        clearUserData(state);
      })
      .addCase(handleUserLogout.rejected, (state, action) => {
        clearUserData(state);
      })
      .addCase(authMe.fulfilled, (state, action) => {
        updateUserAndPermissions(state, action);
      })
      .addCase(authMe.rejected, (state, action) => {
        /* const errorMessage = action.error?.message;
        if (!ignoredErrors.includes(errorMessage)) {
          clearUserData(state);
        } */
      })
      // .addCase(refreshToken.fulfilled, (state, action) => {
      //   const data = action.payload
      //   console.log(data);
      // })
      // .addCase(refreshToken.rejected, (state, action) => {
      //   console.log(action.payload, action.error);
      // })
  }
});

export const { setTokens, clearAuth } = loginUserSlice.actions;

export default loginUserSlice.reducer;
